/**
 * Admin manager for the unit's shared mission areas: list rows with a kind
 * badge, delete with inline confirmation, and add the base-location preset
 * areas that aren't present yet (same presets as the first-run setup step).
 */

import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import type { AreaKind, MissionArea } from '@vtak/shared';
import { cn } from '@/lib/utils';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAreasQuery, useCreateArea, useDeleteArea } from '@/queries/use-area-queries';
import { useLocationsQuery } from '@/queries/use-location-queries';
import {
  derivePresetAreas,
  missingPresetAreas,
  sharedAreas,
} from '@/components/workspace/area-presets';

/** Badge label per area kind. */
const KIND_LABELS: Record<AreaKind, string> = {
  area: 'AO',
  'hazard-zone': 'hazard',
  closure: 'closure',
};

/** Badge classes per area kind (mirrors the map layer's kind colors). */
const KIND_CLASSES: Record<AreaKind, string> = {
  area: 'bg-sky-400/15 text-sky-400',
  'hazard-zone': 'bg-amber-400/15 text-amber-400',
  closure: 'bg-red-400/15 text-red-400',
};

/** Admin editor listing/deleting shared mission areas and adding presets. */
export function MissionAreasEditor({ unitId }: { unitId: string }): React.JSX.Element {
  const areas = useAreasQuery(unitId);
  const locations = useLocationsQuery(unitId);
  const create = useCreateArea(unitId);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');

  const shared = sharedAreas(areas.data ?? []);
  const presets = derivePresetAreas(locations.data ?? []);
  const missing = missingPresetAreas(presets, shared);

  async function addPresets(): Promise<void> {
    setAdding(true);
    setError('');
    try {
      for (const preset of missing) {
        await create.mutateAsync({ name: preset.name, kind: 'area', geometry: preset.geometry });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Adding presets failed');
    } finally {
      setAdding(false);
    }
  }

  return (
    <ScrollArea className="flex-1">
      <div className="space-y-3 p-3">
        <p className="text-[11px] text-muted-foreground/80">
          Shared mission areas broadcast to every member's map. Draw custom shapes via Tools →
          Drawing → Save as Mission Area.
        </p>

        {shared.map((area) => (
          <AreaRow key={area.id} area={area} unitId={unitId} />
        ))}
        {shared.length === 0 && !areas.isLoading && (
          <p className="text-xs text-muted-foreground">No shared mission areas yet.</p>
        )}

        {error && <p className="text-xs text-red-400">{error}</p>}
        <button
          type="button"
          disabled={adding || missing.length === 0}
          onClick={() => void addPresets()}
          className="w-full rounded-md border border-echo-border px-3 py-2 text-xs text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
        >
          {adding
            ? 'Adding…'
            : missing.length > 0
              ? `Add ${missing.length} preset area${missing.length === 1 ? '' : 's'} from base locations`
              : 'All base-location presets added'}
        </button>
      </div>
    </ScrollArea>
  );
}

/** One shared-area row: name, kind badge, and a two-step delete button. */
function AreaRow({ area, unitId }: { area: MissionArea; unitId: string }): React.JSX.Element {
  const remove = useDeleteArea(unitId);
  const [confirming, setConfirming] = useState(false);
  // Ancestor units' shared areas are visible here but only deletable from
  // their owning unit — the server scopes deletes to the route's unit.
  const inherited = area.unitId !== unitId;

  return (
    <div className="flex items-center gap-2 rounded-md bg-white/[0.03] px-2 py-1.5">
      <span className="min-w-0 flex-1 truncate text-xs font-medium text-foreground">
        {area.name}
      </span>
      <span
        className={cn(
          'rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest',
          KIND_CLASSES[area.kind],
        )}
      >
        {KIND_LABELS[area.kind]}
      </span>
      {inherited ? (
        <span className="text-[10px] text-muted-foreground/70">inherited</span>
      ) : confirming ? (
        <span className="flex items-center gap-1">
          <button
            type="button"
            disabled={remove.isPending}
            onClick={() => remove.mutate(area.id, { onSettled: () => setConfirming(false) })}
            className="rounded bg-red-500/20 px-2 py-1 text-[10px] font-bold text-red-400 transition-colors hover:bg-red-500/30 disabled:opacity-50"
          >
            Delete
          </button>
          <button
            type="button"
            onClick={() => setConfirming(false)}
            className="rounded px-2 py-1 text-[10px] text-muted-foreground transition-colors hover:text-foreground"
          >
            Cancel
          </button>
        </span>
      ) : (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          aria-label={`Delete ${area.name}`}
          className="rounded p-1 text-muted-foreground transition-colors hover:text-red-400"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
